import React from "react";
import { useNavigate } from "react-router-dom";
import { Thermometer, Flame, Sun, Droplets, Layers, Building2, Check, ArrowRight, Percent, Clock, FileCheck } from "lucide-react";
import { useLang } from "@/i18n";
import { Reveal } from "@/components/site/motion";
import Seo from "@/components/site/Seo";
import ContactForm from "@/components/site/ContactForm";
import Simulatore from "@/components/site/Simulatore";
import { IMAGES } from "@/data/content";

const INT_ICONS = [Thermometer, Flame, Sun, Droplets, Layers, Building2];
const ADV_ICONS = [Percent, Clock, FileCheck];

export default function ContoTermico() {
    const { t } = useLang();
    const c = t.contoTermico;
    const navigate = useNavigate();

    return (
        <article data-testid="conto-termico-page">
            <Seo title={t.seo.contoTermico.t} description={t.seo.contoTermico.d} image={IMAGES.solar} path="/conto-termico" />

            <section className="relative min-h-[70vh] flex items-end pt-40 pb-16 overflow-hidden bg-cb-darker">
                <div className="absolute inset-0">
                    <img src="/projects/solar.jpg?v=2" alt="Conto Termico 3.0" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-cb-darker/80" />
                    <div className="absolute inset-0 bg-gradient-to-t from-cb-darker to-transparent" />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
                    <Reveal className="text-cb-accent text-xs uppercase tracking-[0.3em] mb-4">{c.chapter}</Reveal>
                    <Reveal as="h1" delay={0.3} className="font-display text-4xl md:text-6xl font-bold tracking-tighter text-white max-w-3xl">{c.title}</Reveal>
                    <Reveal delay={0.6} className="mt-5 max-w-2xl text-slate-300 text-lg">{c.sub}</Reveal>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-4xl mx-auto px-6">
                    <Reveal className="text-slate-600 text-lg md:text-xl leading-relaxed">{c.intro}</Reveal>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-cb-dark cb-grid text-white overflow-hidden">
                <div className="max-w-6xl mx-auto px-6 relative">
                    <Reveal className="text-cb-accent text-xs uppercase tracking-[0.3em] mb-4">{t.simulator.title}</Reveal>
                    <Reveal as="h2" delay={0.3} className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-3">{t.simulator.title}</Reveal>
                    <Reveal delay={0.5} className="text-slate-300 max-w-2xl mb-12">{t.simulator.sub}</Reveal>
                    <Reveal delay={0.6}>
                        <Simulatore dark />
                    </Reveal>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-cb-light">
                <div className="max-w-7xl mx-auto px-6">
                    <Reveal as="h2" className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-cb-dark mb-14">{c.interventionsTitle}</Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200">
                        {c.interventions.map((it, i) => {
                            const Icon = INT_ICONS[i % INT_ICONS.length];
                            return (
                                <Reveal key={i} delay={i * 0.3} className="group bg-white p-8 hover:bg-cb-dark transition-colors duration-500">
                                    <Icon size={34} strokeWidth={1.3} className="text-cb-accent" />
                                    <h3 className="mt-6 font-display text-xl font-semibold text-cb-dark group-hover:text-white transition-colors">{it.t}</h3>
                                    <p className="mt-2 text-slate-500 group-hover:text-slate-300 transition-colors leading-relaxed">{it.d}</p>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16">
                    <div>
                        <Reveal as="h2" className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-cb-dark mb-8">{c.beneficiariesTitle}</Reveal>
                        <div className="space-y-4">
                            {c.beneficiaries.map((b, i) => (
                                <Reveal key={i} delay={i * 0.3} className="flex items-center gap-4 border border-slate-200 p-5">
                                    <Check size={22} className="text-cb-accent shrink-0" />
                                    <span className="text-cb-dark font-medium">{b}</span>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Reveal as="h2" className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-cb-dark mb-8">{c.advantagesTitle}</Reveal>
                        <div className="space-y-px bg-slate-200">
                            {c.advantages.map((a, i) => {
                                const Icon = ADV_ICONS[i % ADV_ICONS.length];
                                return (
                                    <Reveal key={i} delay={i * 0.3} className="flex items-start gap-5 bg-cb-light p-6">
                                        <Icon size={28} strokeWidth={1.4} className="text-cb-accent shrink-0" />
                                        <div>
                                            <h3 className="font-display text-lg font-semibold text-cb-dark">{a.t}</h3>
                                            <p className="mt-1 text-slate-500">{a.d}</p>
                                        </div>
                                    </Reveal>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            <section className="relative py-24 md:py-32 bg-cb-dark cb-grid text-white overflow-hidden">
                <div className="max-w-3xl mx-auto px-6 text-center relative">
                    <Reveal as="h2" className="font-display text-3xl md:text-5xl font-bold tracking-tighter">{c.cta}</Reveal>
                    <Reveal delay={0.5}>
                        <button data-testid="conto-termico-cta" onClick={() => navigate("/preventivo")}
                            className="cb-glow mt-8 inline-flex items-center gap-2 bg-cb-accent hover:bg-cb-accentHover text-white px-8 py-4 font-medium transition-colors">
                            {t.nav.quote} <ArrowRight size={18} />
                        </button>
                    </Reveal>
                </div>
            </section>
        </article>
    );
}
